import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useAppDispatch } from "../../store";
import {
  resendVerificationCode,
  verifyCode,
} from "../../store/actions/auth.action";
import ToastMessage from "../../utils/toastMessage";
import { App } from "antd";
import logoImage from "../../assets/images/logo.svg";
import theme from "../../themes";
import { authCardCss, authPageCss } from "../../styles/responsive";

const VerificationCode: React.FC = () => {
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { email } = useLocation().state as { email: string };
  const { message } = App.useApp();
  const toast = ToastMessage(message);

  const isCodeComplete = code.every((c) => c !== "");

  // Start countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  const handleCodeChange = (index: number, value: string) => {
    // Only allow single digit numbers
    if (value.length > 1 || !/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    // Only allow numeric keys, backspace, delete, arrow keys, and tab
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Tab",
    ];
    const isNumeric = /^\d$/.test(e.key);

    if (!isNumeric && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text/plain")
      .replace(/\D/g, "")
      .slice(0, 6);
    const newCode = [...code];

    for (let i = 0; i < 6; i++) {
      newCode[i] = pastedData[i] || "";
    }

    setCode(newCode);

    // Focus last filled input or first empty input
    const lastFilledIndex = Math.min(pastedData.length - 1, 5);
    inputRefs.current[lastFilledIndex]?.focus();
  };

  const handleVerify = async () => {
    const verificationCode = code.join("");
    if (verificationCode.length !== 6) {
      toast.error("Please enter the complete 6-digit code", 5);
      return;
    }

    setLoading(true);
    setError(false);
    try {
      const response = await dispatch(
        verifyCode({ email, verification_code: verificationCode })
      ).unwrap();

      // Check if user is admin and redirect to company selection
      if (response.data?.is_super_admin) {
        navigate("/company-selection");
      } else {
        navigate("/");
      }
    } catch (error) {
      setError(true);
      toast.error(
        "The code can't be verified. Please try again or request a new code.",
        5
      );
      setCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;

    setCanResend(false);
    setCountdown(60);

    try {
      await dispatch(resendVerificationCode({ email })).unwrap();
      toast.success("Verification code sent to your email");
    } catch (error) {
      toast.error("Failed to send verification code. Please try again.");
      setCanResend(true);
      setCountdown(0);
    }
  };

  return (
    <Container>
      <LogoContainer>
        <LogoWrapper>
          <LogoImage src={logoImage} alt="Lytte.ai Logo" />
          <Title>Lytte.ai</Title>
        </LogoWrapper>
      </LogoContainer>

      <Card>
        <PageTitle>Verify your email</PageTitle>
        <Subtitle>
          A 6-digit passcode has been sent to your email. Please check your
          email and enter the code below to verify.
        </Subtitle>

        <InputsRow>
          <CodeContainer>
            {code.map((digit, index) => (
              <CodeInput
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                type="number"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                min="0"
                max="9"
                step="1"
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onKeyPress={(e) => handleKeyPress(e)}
                onPaste={handlePaste}
                disabled={loading}
                className={error ? "error" : ""}
              />
            ))}
          </CodeContainer>

          {canResend ? (
            <ResendButton onClick={handleResend}>Resend code</ResendButton>
          ) : (
            <ResendButton disabled>Resend code ({countdown}s)</ResendButton>
          )}
        </InputsRow>

        <Divider />

        <FooterRow>
          <BackToLoginLink to="/">Back to login</BackToLoginLink>
          <VerifyButton
            onClick={handleVerify}
            disabled={loading || !isCodeComplete}
          >
            {loading ? (
              <>
                <LoadingSpinner />
                Verifying...
              </>
            ) : (
              "Verify"
            )}
          </VerifyButton>
        </FooterRow>
      </Card>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #fff;
  ${authPageCss}
`;

const LogoContainer = styled.div`
  text-align: center;
  margin-bottom: 48px;

  @media (max-width: 640px) {
    margin-bottom: 24px;
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const LogoImage = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
`;

const Title = styled.h1`
  color: #333;
  text-align: center;
  margin: 0;
  font-size: 20px;
  font-weight: 500;
  font-style: italic;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  ${authCardCss}
`;

const PageTitle = styled.h2`
  color: #333;
  text-align: left;
  margin: 0 0 12px 0;
  font-size: 20px;
  font-weight: 700;
`;

const Subtitle = styled.p`
  color: #333;
  text-align: left;
  margin: 0 0 24px 0;
  font-size: 18px;
  line-height: 1.5;
  max-width: 560px;
`;

const InputsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const CodeContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const CodeInput = styled.input`
  width: 42px;
  height: 40px;
  flex: 0 0 42px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  transition: all 0.2s ease;
  background: white;

  @media (max-width: 400px) {
    width: 36px;
    height: 36px;
    flex-basis: 36px;
    font-size: 16px;
  }
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  transition: all 0.2s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: #00b4d8;
    box-shadow: 0 0 0 2px rgba(0, 180, 216, 0.1);
  }

  &:hover {
    border-color: #cfd4da;
  }

  &.error {
    border-color: #e74c3c;
    box-shadow: 0 0 0 2px rgba(231, 76, 60, 0.1);
  }

  /* Hide spinner for number inputs */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  &[type="number"] {
    -moz-appearance: textfield;
    appearance: textfield;
  }
`;

const ResendButton = styled.button`
  background: none;
  border: none;
  color: #36a3e8;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.2s ease;

  &:hover:not(:disabled) {
    color: #2980b9;
  }

  &:disabled {
    color: #bdbdbd;
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background: #e5e7eb;
  margin: 32px -16px 24px -16px;
`;

const FooterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const BackToLoginLink = styled(Link)`
  color: #9aa3af;
  font-size: 16px;
  font-weight: 400;
  text-decoration: none;
`;

const VerifyButton = styled.button`
  background: ${theme.colors.green1};
  color: #fff;
  border: none;
  border-radius: 8px;
  height: 40px;
  padding: 0 16px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #047857 !important;
    color: #fff !important;
  }

  &:disabled {
    background: ${theme.colors.grey2};
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export default VerificationCode;
